import { TextField } from '@kanos/ui';
import { Page } from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { HELP_ARTICLES, type HelpArticle } from '../help/articles';
import { FaqList, faqQuery, matchesSearch } from '../help/faq';

export const Route = createFileRoute('/help/')({
  component: HelpOverview,
});

/** What the search found, for screen readers: „1 Artikel und 2 Fragen gefunden“. */
const found = (articles: number, questions: number) =>
  `${String(articles)} Artikel und ${String(questions)} ${questions === 1 ? 'Frage' : 'Fragen'} gefunden`;

/** Everything an article says, for the search. */
const textOf = (article: HelpArticle) =>
  [
    article.title,
    article.summary,
    ...article.sections.flatMap((s) => [s.heading, ...s.paragraphs]),
  ].join(' ');

function ArticleList({ articles }: { articles: readonly HelpArticle[] }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {articles.map((article) => (
        <li key={article.slug}>
          <Link
            to="/help/$slug"
            params={{ slug: article.slug }}
            className="glass flex h-full items-center gap-4 rounded-lg p-5 outline-none transition-colors hover:bg-fill focus-visible:outline-2 focus-visible:outline-focus"
          >
            <span className="grid min-w-0 flex-1 gap-1">
              <span className="font-semibold">{article.title}</span>
              <span className="text-sm text-muted">{article.summary}</span>
            </span>
            <ChevronRight aria-hidden className="size-4 shrink-0 text-muted" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Every help article, and a search through them and the questions people ask. */
function HelpOverview() {
  const faq = useQuery(faqQuery);
  const [search, setSearch] = useState('');
  const term = search.trim();
  const articles = term
    ? HELP_ARTICLES.filter((a) => matchesSearch(textOf(a), term))
    : HELP_ARTICLES;
  const questions = term
    ? (faq.data ?? []).filter((e) => matchesSearch(`${e.question} ${e.answer}`, term))
    : [];
  return (
    <Page title="Hilfe" subtitle="Wie Kanos arbeitet – Bereich für Bereich.">
      <TextField
        className="max-w-xl"
        label="Hilfe durchsuchen"
        placeholder="z. B. Datenexport"
        type="search"
        value={search}
        onChange={setSearch}
      />
      <p className="sr-only" aria-live="polite">
        {term ? found(articles.length, questions.length) : ''}
      </p>
      {articles.length > 0 && <ArticleList articles={articles} />}
      {questions.length > 0 && (
        <section aria-labelledby="faq-hits" className="grid gap-3">
          <h2 id="faq-hits" className="text-lg font-semibold tracking-[-0.02em]">
            Häufige Fragen
          </h2>
          <FaqList entries={questions} />
        </section>
      )}
      {term && articles.length === 0 && questions.length === 0 && (
        <p className="text-muted">
          Nichts zu „{term}“ gefunden. Versuchen Sie ein anderes Wort oder sehen Sie die Artikel
          durch.
        </p>
      )}
    </Page>
  );
}
